import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/common.css';
import { API_BASE_URL } from '../services/api';

const TIER_STYLE = {
  bronze: { label: 'BRONZE', bg: 'linear-gradient(135deg, #cd7f32 0%, #e8a96a 100%)', border: '#cd7f32', text: '#7a4a1a' },
  silver: { label: 'SILVER', bg: 'linear-gradient(135deg, #9e9e9e 0%, #d4d4d4 100%)', border: '#9e9e9e', text: '#4a4a4a' },
  gold:   { label: 'GOLD',   bg: 'linear-gradient(135deg, #f5a623 0%, #ffd700 100%)', border: '#f5a623', text: '#7a5000' }
};

const CATEGORY_LABEL = {
  mission:  '미션 달성',
  acorn:    '도토리',
  streak:   '연속 달성',
  perfect:  '완벽 달성',
  champion: '챔피언'
};

function BadgeItem({ badge, onClick }) {
  const tier = badge.tier ? TIER_STYLE[badge.tier] : null;
  const pct = badge.total > 0 ? Math.min((badge.progress / badge.total) * 100, 100) : 0;

  return (
    <div
      className={`badge-item ${badge.earned ? 'earned' : 'locked'}`}
      style={badge.earned && tier ? { background: tier.bg, borderColor: tier.border } : {}}
      onClick={() => onClick(badge)}
    >
      <div className="badge-icon-wrap">
        <span className="badge-icon">{badge.earned ? badge.icon : '🔒'}</span>
        {tier && (
          <span className="tier-label" style={badge.earned ? { background: tier.border, color: '#fff' } : {}}>
            {tier.label}
          </span>
        )}
      </div>
      <div className="badge-info">
        <h4>{badge.name}</h4>
        <p>{badge.condition}</p>
        {!badge.earned && (
          <>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="progress-text">{badge.progress.toLocaleString()} / {badge.total.toLocaleString()}</span>
          </>
        )}
        {badge.earned && badge.earnedAt && (
          <span className="earned-date">
            {new Date(badge.earnedAt).toLocaleDateString('ko-KR')} 획득
          </span>
        )}
      </div>
    </div>
  );
}

function LevelCard({ level }) {
  if (!level) return null;
  const pct = level.isMax ? 100 : Math.min((level.progressInLevel / level.rangeSize) * 100, 100);

  return (
    <div className="level-card">
      <div className="level-left">
        <span className="level-icon">{level.icon}</span>
        <div>
          <div className="level-num">Lv.{level.level}</div>
          <div className="level-title">{level.title}</div>
        </div>
      </div>
      <div className="level-right">
        <div className="level-progress-header">
          <span className="level-missions">{level.approvedCount.toLocaleString()}개 완료</span>
          {!level.isMax && (
            <span className="level-next">다음: {level.nextTitle} ({level.nextMin}개~)</span>
          )}
          {level.isMax && <span className="level-max">최고 등급!</span>}
        </div>
        <div className="level-bar">
          <div className="level-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function BadgeModal({ badge, onClose }) {
  if (!badge) return null;
  const tier = badge.tier ? TIER_STYLE[badge.tier] : null;
  const pct = badge.total > 0 ? Math.min((badge.progress / badge.total) * 100, 100) : 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>배지 상세</h3>
          <button onClick={onClose} className="modal-close">×</button>
        </div>
        <div className="modal-body">
          <div className="modal-badge-icon">{badge.earned ? badge.icon : '🔒'}</div>
          {tier && (
            <div className="modal-tier" style={{ background: tier.bg, color: tier.text }}>
              {tier.label}
            </div>
          )}
          <h4>{badge.name}</h4>
          <p className="badge-description">{badge.description}</p>

          {badge.earned ? (
            <div className="earned-status">
              <span>✅ 획득 완료!</span>
              {badge.earnedAt && (
                <span className="modal-earned-date">
                  {new Date(badge.earnedAt).toLocaleDateString('ko-KR')}
                </span>
              )}
            </div>
          ) : (
            <div className="progress-section">
              <div className="progress-info">
                <span>진행률: {badge.progress.toLocaleString()} / {badge.total.toLocaleString()}</span>
                <span>{Math.floor(pct)}%</span>
              </div>
              <div className="modal-progress-bar">
                <div className="modal-progress-fill" style={{ width: `${pct}%` }} />
              </div>
              <p className="progress-hint">
                {(badge.total - badge.progress).toLocaleString()}개 더 필요해요!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChildBadges() {
  const [user, setUser] = useState(null);
  const [badgeData, setBadgeData] = useState({ badges: [], earnedCount: 0, totalCount: 0, level: null });
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  const fetchBadges = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/badges/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) { navigate('/child/login'); return; }
      if (!res.ok) throw new Error('배지 조회 실패');
      const data = await res.json();
      setBadgeData(data);
    } catch (err) {
      setError('배지를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/child/login'); return; }
    try {
      const parsed = JSON.parse(userData);
      if (parsed.type !== 'child') { navigate('/'); return; }
      setUser(parsed);
      fetchBadges(token);
    } catch {
      navigate('/child/login');
    }
  }, [navigate, fetchBadges]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user || loading) {
    return (
      <div className="page-container" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ textAlign: 'center', padding: '40px', color: 'white', fontSize: '1.1rem' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="page-header">
          <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
          <h1>🏆 뱃지</h1>
          <div />
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
          <p>{error}</p>
          <button onClick={() => fetchBadges(localStorage.getItem('token'))} className="btn">다시 시도</button>
        </div>
      </div>
    );
  }

  const categories = ['all', 'mission', 'acorn', 'streak', 'perfect', 'champion'];
  const filteredBadges = activeCategory === 'all'
    ? badgeData.badges
    : badgeData.badges.filter(b => b.category === activeCategory);

  return (
    <div className="page-container" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>🏆 뱃지</h1>
        <button onClick={handleLogout} className="btn btn-outline btn-sm">로그아웃</button>
      </div>

      <div className="page-content" style={{ maxWidth: 600, margin: '0 auto', padding: '16px' }}>

        {/* 레벨 카드 */}
        <LevelCard level={badgeData.level} />

        {/* 요약 */}
        <div className="summary-card">
          <div className="summary-item">
            <span className="summary-icon">🏅</span>
            <div>
              <div className="summary-label">획득한 배지</div>
              <div className="summary-value">{badgeData.earnedCount}개</div>
            </div>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="summary-icon">🎯</span>
            <div>
              <div className="summary-label">전체 배지</div>
              <div className="summary-value">{badgeData.totalCount}개</div>
            </div>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span className="summary-icon">📊</span>
            <div>
              <div className="summary-label">달성률</div>
              <div className="summary-value">
                {badgeData.totalCount > 0
                  ? Math.floor((badgeData.earnedCount / badgeData.totalCount) * 100)
                  : 0}%
              </div>
            </div>
          </div>
        </div>

        {/* 카테고리 탭 */}
        <div className="category-tabs">
          {categories.map(cat => (
            <button
              key={cat}
              className={`cat-tab ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === 'all' ? '전체' : CATEGORY_LABEL[cat]}
            </button>
          ))}
        </div>

        {/* 배지 목록 */}
        <div className="badges-card">
          <div className="badges-grid">
            {filteredBadges.map(badge => (
              <BadgeItem key={badge.code} badge={badge} onClick={setSelectedBadge} />
            ))}
          </div>
        </div>
      </div>

      {/* 배지 상세 모달 */}
      <BadgeModal badge={selectedBadge} onClose={() => setSelectedBadge(null)} />

      <style>{`
        .page-header {
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255,255,255,0.2);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .page-header h1 { margin: 0; color: #764ba2; font-size: 1.3rem; font-weight: bold; }

        /* 레벨 카드 */
        .level-card {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          border-radius: 16px;
          padding: 18px 20px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          color: white;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }
        .level-left { display: flex; align-items: center; gap: 12px; min-width: 90px; }
        .level-icon { font-size: 2.8rem; }
        .level-num { font-size: 0.75rem; color: #aaa; font-weight: 600; }
        .level-title { font-size: 1.3rem; font-weight: bold; color: #ffd700; }
        .level-right { flex: 1; }
        .level-progress-header { display: flex; justify-content: space-between; font-size: 0.8rem; margin-bottom: 8px; }
        .level-missions { color: #ccc; }
        .level-next { color: #888; }
        .level-max { color: #ffd700; font-weight: bold; }
        .level-bar { width: 100%; height: 8px; background: rgba(255,255,255,0.15); border-radius: 4px; overflow: hidden; }
        .level-bar-fill { height: 100%; background: linear-gradient(90deg, #667eea, #ffd700); border-radius: 4px; transition: width 0.4s; }

        /* 요약 */
        .summary-card {
          background: white; border-radius: 14px; padding: 16px 20px;
          display: flex; align-items: center; margin-bottom: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }
        .summary-item { display: flex; align-items: center; gap: 10px; flex: 1; }
        .summary-icon { font-size: 1.8rem; }
        .summary-label { font-size: 0.78rem; color: #888; }
        .summary-value { font-size: 1.5rem; font-weight: bold; color: #764ba2; line-height: 1.1; }
        .summary-divider { width: 1px; height: 40px; background: #eee; margin: 0 8px; }

        /* 카테고리 탭 */
        .category-tabs {
          display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px;
          margin-bottom: 14px; scrollbar-width: none;
        }
        .category-tabs::-webkit-scrollbar { display: none; }
        .cat-tab {
          background: rgba(255,255,255,0.25); border: none; color: white;
          padding: 7px 14px; border-radius: 20px; font-size: 0.82rem;
          white-space: nowrap; cursor: pointer; transition: all 0.2s; font-weight: 500;
        }
        .cat-tab.active { background: white; color: #764ba2; font-weight: bold; }

        /* 배지 카드 */
        .badges-card { background: white; border-radius: 14px; padding: 16px; box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
        .badges-grid { display: flex; flex-direction: column; gap: 10px; }
        .badge-item {
          display: flex; align-items: center; gap: 14px; padding: 14px;
          border-radius: 12px; cursor: pointer; border: 2px solid transparent;
          transition: all 0.2s;
        }
        .badge-item.earned { background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%); border-color: #fdcb6e; }
        .badge-item.locked { background: #f8f9fa; border-color: #e9ecef; }
        .badge-item:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
        .badge-icon-wrap { position: relative; min-width: 50px; text-align: center; }
        .badge-icon { font-size: 2.2rem; }
        .tier-label {
          position: absolute; bottom: -6px; left: 50%; transform: translateX(-50%);
          font-size: 0.55rem; font-weight: bold; padding: 1px 5px; border-radius: 6px;
          background: #ccc; color: #444; white-space: nowrap;
        }
        .badge-info { flex: 1; }
        .badge-info h4 { margin: 0 0 2px; font-size: 1rem; color: #333; }
        .badge-info p { margin: 0 0 6px; font-size: 0.82rem; color: #666; }
        .progress-bar { width: 100%; height: 5px; background: #e9ecef; border-radius: 3px; overflow: hidden; margin-bottom: 3px; }
        .progress-fill { height: 100%; background: linear-gradient(90deg, #667eea, #764ba2); border-radius: 3px; transition: width 0.4s; }
        .progress-text { font-size: 0.75rem; color: #888; }
        .earned-date { font-size: 0.75rem; color: #7a5000; font-weight: 500; }

        /* 모달 */
        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.55);
          display: flex; justify-content: center; align-items: center; z-index: 1000;
        }
        .modal-content {
          background: white; border-radius: 18px; width: 90%; max-width: 380px;
          max-height: 85vh; overflow-y: auto;
        }
        .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 18px 20px; border-bottom: 1px solid #eee;
        }
        .modal-header h3 { margin: 0; color: #333; }
        .modal-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #999; }
        .modal-body { padding: 24px 20px; text-align: center; }
        .modal-badge-icon { font-size: 4rem; margin-bottom: 10px; }
        .modal-tier {
          display: inline-block; padding: 3px 14px; border-radius: 20px;
          font-size: 0.8rem; font-weight: bold; margin-bottom: 10px;
        }
        .modal-body h4 { margin: 0 0 8px; font-size: 1.2rem; color: #333; }
        .badge-description { color: #666; margin-bottom: 18px; font-size: 0.9rem; }
        .earned-status {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          padding: 14px; background: #d4edda; border-radius: 10px; color: #155724; font-weight: bold;
        }
        .modal-earned-date { font-size: 0.82rem; font-weight: normal; color: #387a50; }
        .progress-section { text-align: left; }
        .progress-info { display: flex; justify-content: space-between; font-size: 0.85rem; color: #666; margin-bottom: 8px; }
        .modal-progress-bar { width: 100%; height: 10px; background: #e9ecef; border-radius: 5px; overflow: hidden; margin-bottom: 12px; }
        .modal-progress-fill { height: 100%; background: linear-gradient(90deg, #667eea, #764ba2); border-radius: 5px; transition: width 0.4s; }
        .progress-hint { text-align: center; color: #888; font-size: 0.88rem; margin: 0; }
      `}</style>
    </div>
  );
}

export default ChildBadges;
